import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, getTrackByFn, InputObservable } from '@y42/angular/util';
import { DatepickerRange, DateRangeConfig } from '@y42/core/daterange-picker';
import { DateTimeService } from '@y42/date-time';
import { ColumnTypeEnum, OperatorEnum, OperatorTypeEnum, OperatorValueTypeEnum } from '@y42/json-schema';
import { isNotNullish } from '@y42/util/nullish';
import { SelectOption } from '@y42/util/select-option';
import { filter, map, Observable, startWith, switchMap, tap, withLatestFrom } from 'rxjs';
import { OperationInputFormService } from '../../../operation-input-form.service';
import { AbsoluteDateRangePreset, OperatorForm, RelativeDateRangePreset } from '../../../operation-input.model';
import { availableDateOperatorValueTypes, operatorValueTypeToText } from '../operation-input-date-time.model';
import { defaultAbsoluteRangePresets, defaultRelativeRangePresets } from './date-range-presets';

@UntilDestroy()
@Component({
  selector: 'y42-operation-input-date-time-form-group[form][columnType][index]',
  templateUrl: './operation-input-date-time-form-group.component.html',
  styleUrls: ['../operation-input-date-time.component.scss', '../../operation-input-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OperationInputDateTimeFormGroupComponent {
  constructor(
    private readonly formService: OperationInputFormService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly dateTimeService: DateTimeService,
  ) {
    this.formService.reset$
      .pipe(
        tap(() => {
          this.setDates({ start: null, end: null });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  @Input()
  @InputObservable()
  form!: FormGroup<OperatorForm>;
  private readonly form$!: Observable<FormGroup<OperatorForm> | undefined>;

  @Input()
  index!: number;

  @Input()
  columnType!: ColumnTypeEnum;

  @Input()
  showRelativeDates = false;

  @Input()
  enableMoreConditions = true;

  @Input()
  @InputObservable()
  absoluteRanges?: AbsoluteDateRangePreset[];
  absoluteRanges$!: Observable<AbsoluteDateRangePreset[] | undefined>;

  @Input()
  @InputObservable()
  relativeRanges?: RelativeDateRangePreset[];
  relativeRanges$!: Observable<RelativeDateRangePreset[] | undefined>;

  @Input()
  @InputObservable()
  operatorOptions?: Array<SelectOption<OperatorEnum>>;
  operatorOptions$!: Observable<Array<SelectOption<OperatorEnum>> | undefined>;

  @Input()
  @InputObservable()
  minDate?: Date;
  minDate$!: Observable<Date | undefined>;

  @Input()
  @InputObservable()
  maxDate?: Date;
  maxDate$!: Observable<Date | undefined>;

  readonly trackByValueProp = getTrackByFn<{ value: string; text: string }>('value');
  readonly trackByDateRangePreset = getTrackByFn<RelativeDateRangePreset>('label');

  readonly operationsFormArray = this.formService.operationsFormArray;

  readonly operatorValueTypes = availableDateOperatorValueTypes;

  readonly Operator = OperatorEnum;
  readonly OperatorType = OperatorTypeEnum;
  readonly ColumnType = ColumnTypeEnum;

  private readonly operator$ = this.form$.pipe(
    filter(Boolean),
    switchMap((form) => form.controls.operator!.valueChanges.pipe(startWith(form.controls.operator!.value))),
  );

  private readonly operatorType$ = this.form$.pipe(
    filter(Boolean),
    switchMap((form) =>
      form.controls.operator_type!.valueChanges.pipe(
        // Reset values when operatorType changes
        tap(() => form.controls.values.setValue([])),

        // tap(() => this.setRange({ dates: [] })),
        startWith(form.controls.operator_type!.value),
      ),
    ),
  );

  private readonly operatorValueType$ = this.form$.pipe(
    filter(Boolean),
    switchMap((form) => form.controls.value_type!.valueChanges.pipe(startWith(form.controls.value_type!.value))),
  );

  private readonly values$ = this.form$.pipe(
    filter(Boolean),
    switchMap((form) => form.controls.values!.valueChanges.pipe(startWith(form.controls.values!.value))),
  );

  readonly absolutePresets: Observable<AbsoluteDateRangePreset[]> = this.absoluteRanges$.pipe(
    map((inputRanges) => inputRanges ?? defaultAbsoluteRangePresets),
  );

  readonly relativePresets$: Observable<RelativeDateRangePreset[]> = this.relativeRanges$.pipe(
    map((inputRanges) => inputRanges ?? defaultRelativeRangePresets),
  );

  readonly matchedRelativePreset$: Observable<RelativeDateRangePreset | undefined> = this.form$.pipe(
    filter(isNotNullish),
    switchMap((form) => form.valueChanges.pipe(startWith(form.value))),
    withLatestFrom(this.relativePresets$),
    map(([{ value_type, values }, presets]) =>
      presets.find(
        (preset) => values && preset.valueType === value_type && preset.from === values[0] && preset.to === values[1],
      ),
    ),
  );

  private readonly defaultOperatorOptions: Array<SelectOption<OperatorEnum>> = [
    { value: OperatorEnum.Between, label: 'is between' },
    { value: OperatorEnum.LessThan, label: 'is before' },
    { value: OperatorEnum.GreaterThan, label: 'is after' },
    { value: OperatorEnum.IsEmpty, label: 'is empty' },
    { value: OperatorEnum.IsNotEmpty, label: 'is not empty' },
    { value: OperatorEnum.Equals, label: 'is equal' },
  ];

  readonly dateNow = new Date();
  readonly ranges: DateRangeConfig[] = [
    {
      label: 'Today',
      start: this.dateNow,
      end: this.dateNow,
    },
    {
      label: 'Last 7 Days',
      start: this.dateTimeService.subtract(this.dateNow, { days: 6 }),
      end: this.dateNow,
    },
    {
      label: 'Last 30 Days',
      start: this.dateTimeService.subtract(this.dateNow, { days: 29 }),
      end: this.dateNow,
    },
    {
      label: 'This Month',
      start: this.dateTimeService.startOfMonth(this.dateNow),
      end: this.dateTimeService.endOfMonth(this.dateNow),
    },
    {
      label: 'Last Month',
      start: this.dateTimeService.startOfMonth(this.dateTimeService.subtract(this.dateNow, { months: 1 })),
      end: this.dateTimeService.endOfMonth(this.dateTimeService.subtract(this.dateNow, { months: 1 })),
    },
  ];

  readonly state = connectState(this, {
    operator: this.operator$,
    operatorType: this.operatorType$,
    operatorValueTypeLabel: this.operatorValueType$.pipe(
      map((value) => operatorValueTypeToText?.[value as OperatorValueTypeEnum] || ''),
    ),
    values0: this.values$.pipe(map((values) => values?.[0])),
    values1: this.values$.pipe(map((values) => values?.[1])),
    initialDates: this.form$.pipe(
      filter(Boolean),
      startWith(null),
      map(() => {
        const { values, operator } = this.form?.value ?? {};
        return {
          startDate: (values?.[0] as Date) ?? this.dateNow,
          endDate: operator === OperatorEnum.Between ? (values?.[1] as Date) : (values?.[0] as Date) ?? this.dateNow,
        };
      }),
    ),
    minDate: this.minDate$.pipe(map((date) => date ?? this.dateTimeService.parseDate('19700101', 'yyyyMMdd'))),
    maxDate: this.maxDate$.pipe(map((date) => date ?? this.dateTimeService.parseDate('30000101', 'yyyyMMdd'))),
    absoluteRanges: this.absolutePresets.pipe(
      map((presets) => presets.reduce((record, { label, start, end }) => ({ ...record, [label]: [start, end] }), {})),
    ),
    relativePresets: this.relativePresets$,
    matchedRelativePreset: this.matchedRelativePreset$,
    operatorOptions: this.operatorOptions$.pipe(map((options) => options ?? this.defaultOperatorOptions)),
  });

  selectOperatorType(change: MatButtonToggleChange) {
    const form = this.form;
    if (change.value === OperatorTypeEnum.Relative) {
      const initialValue = this.isGreaterThanOrLessThanOperator() ? ['-30'] : ['-30', '0'];
      form.controls.values.setValue(initialValue);
      form.controls.value_type?.setValue(OperatorValueTypeEnum.Days);
    } else {
      form.controls.values.setValue([new Date(), new Date()]);
      form.controls.value_type?.setValue(OperatorValueTypeEnum.None);
    }
  }

  setRelativeDate(index: number, relativeDate: string) {
    const valuesCtrl = this.form.controls.values;
    const values = valuesCtrl.value;
    values[index] = relativeDate;
    valuesCtrl.setValue(this.isGreaterThanOrLessThanOperator() ? [relativeDate] : values);
    this.cdRef.markForCheck();
  }

  applyRelativePreset({ valueType, from, to }: RelativeDateRangePreset) {
    this.form.patchValue({
      value_type: valueType,
      values: [from, to],
    });
  }

  setDates({ start, end }: DatepickerRange): void {
    if (this.state.operator === OperatorEnum.Between) {
      this.form.controls.values.setValue([start, end].filter(Boolean));
    } else {
      this.form.controls.values.setValue([start]);
    }
  }

  private isGreaterThanOrLessThanOperator(): boolean {
    const operator = this.form.controls.operator?.value;
    return operator ? [OperatorEnum.GreaterThan, OperatorEnum.LessThan].includes(operator) : false;
  }
}
